// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions, Placement, ShiftOptions } from "@floating-ui/dom";

import { xHeaderUpdate } from "@awc/App/Handlers/XNG/Header";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { MenuVariants } from "@awc/MobiusOverrides/Menu";
import { addScrollbarGutter, getSystemZIndex, removeScrollbarGutter, xClassNames } from "@awc/XDL/Utilities";
import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    safePolygon,
    shift,
    size,
    useClick,
    useDelayGroup,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFloatingTree,
    useHover,
    UseHoverProps,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
    useTransitionStyles,
    useTypeahead,
} from "@floating-ui/react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

/**
 * Please add the style to xdl/globalStyle
 */
export const menuBodyStyle = css`
    .Menu {
        outline: 0;
        box-sizing: border-box;
        *:focus-visible {
            outline: none;
        }
    }
`;

const MenuContext = React.createContext<{
    getItemProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>;
    activeIndex: number | null;
    setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>;
    setHasFocusInside: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
}>({
    getItemProps: () => ({}),
    activeIndex: null,
    setActiveIndex: () => {},
    setHasFocusInside: () => {},
    isOpen: false,
});

interface MenuProps {
    menuTrigger: React.ReactElement;
    /**
     * Primary: The submenu appears at the same level as the parent, i.e., they have the same 'top' value.
     * Secondary: The submenu appears at the level of the item that triggered it.
     */
    variant?: MenuVariants;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    /**
     * Determines whether to show or hide the gray overlay behind the menu.
     */
    overlay?: boolean;
    placement?: Placement;
    children?: React.ReactNode;
    delay?: UseHoverProps["delay"];
}

export const MenuComponent = React.forwardRef<HTMLButtonElement, MenuProps & React.HTMLProps<HTMLButtonElement>>(
    (
        {
            children,
            menuTrigger,
            variant = "primary",
            offset: offsetProps,
            shift: shiftProps,
            overlay,
            placement: placementProps,
            delay: userDelay,
            ...props
        },
        forwardedRef,
    ) => {
        const [isOpen, setIsOpen] = React.useState(false);
        const [hasFocusInside, setHasFocusInside] = React.useState(false);
        const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

        const elementsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
        const labelsRef = React.useRef<Array<string | null>>([]);
        const parent = React.useContext(MenuContext);

        const tree = useFloatingTree();
        const nodeId = useFloatingNodeId();
        const parentId = useFloatingParentNodeId();
        const item = useListItem();
        const dispatch = useDispatch();

        const isNested = parentId !== null;

        // XNG-Changes
        const autoCompleteOpen = useAppSelector(state => (state as CustomAppState).xCommon.autoCompleteOpen);

        const findMenuParent = (elements: HTMLElement): HTMLElement => {
            return elements.closest('[role="menu"]') as HTMLElement;
        };

        useEffect(() => {
            if (!isNested && overlay) {
                if (isOpen) {
                    dispatch(xHeaderUpdate({ key: "registerMenu", value: `${nodeId}_popup` }));
                    addScrollbarGutter();
                    ariaAppHider.hide(document.body);
                } else {
                    dispatch(xHeaderUpdate({ key: "unregisterMenu", value: `${nodeId}_popup` }));
                    removeScrollbarGutter();
                    ariaAppHider.show(document.body);
                }
            }
        }, [isOpen]);

        const findMenuParentList = (elements: HTMLElement): HTMLElement => {
            return elements.closest('[role="menu"] ul') as HTMLElement;
        };

        const { floatingStyles, refs, context } = useFloating<HTMLButtonElement>({
            nodeId,
            open: isOpen,
            onOpenChange: setIsOpen,
            transform: false,
            placement: placementProps,
            middleware: [
                offset(offsetProps),
                flip(),
                shift(
                    variant === "primary"
                        ? shiftProps
                        : {
                              ...shiftProps,
                              boundary: "clippingAncestors",
                          },
                ),
                size(_ => {
                    return {
                        apply({ elements }) {
                            elements.floating.style.zIndex = `${getSystemZIndex("floatingUi")}`;
                            if (isNested && variant === "primary") {
                                const menuParentElement = findMenuParentList(elements.reference as HTMLElement);
                                if (menuParentElement) {
                                    // Calculate height based on current menu's li elements
                                    const currentMenuLis = elements.floating.querySelectorAll("li");
                                    if (currentMenuLis.length > 0) {
                                        const firstLi = currentMenuLis[0];
                                        const liHeight = firstLi.offsetHeight;
                                        const calculatedHeight = liHeight * currentMenuLis.length;
                                        const parentHeight = menuParentElement.clientHeight;

                                        const viewportHeight = window.innerHeight;
                                        const maxViewportHeight = viewportHeight * 0.8;

                                        // If calculated height is less than parent height, use parent height
                                        const finalHeight =
                                            calculatedHeight < parentHeight ? parentHeight : calculatedHeight;

                                        if (finalHeight <= maxViewportHeight) {
                                            elements.floating.style.minHeight = `${finalHeight}px`;
                                            elements.floating.style.maxHeight = `${finalHeight}px`;
                                        } else {
                                            elements.floating.style.maxHeight = `${maxViewportHeight}px`;
                                        }

                                        elements.floating.style.height = "100%";
                                    }
                                }
                            }
                        },
                    };
                }),
                {
                    name: "updateSubMenuPosition",
                    fn({ x, y, elements }) {
                        let xAxis = x;
                        let yAxis = y;

                        /**
                         * Fix for Floating UI's use of clientWidth, which excludes scrollbar width or padding-right.
                         * This code ensures consistent alignment by accounting for these discrepancies.
                         */
                        if (isNested) {
                            const menuStyleParentElement = (elements.reference as HTMLElement).closest(
                                '[class^="MenuStyle"]',
                            ) as HTMLElement;

                            const offsetX = menuStyleParentElement
                                ? elements.floating.offsetWidth - menuStyleParentElement.clientWidth
                                : 0;
                            xAxis = x + offsetX;
                        }

                        /**
                         * Ensures the subMenu height matches the height of its parent menu.
                         */
                        if (isNested && variant === "primary") {
                            const menuParentElement = findMenuParent(elements.reference as HTMLElement);
                            const topValue = menuParentElement ? menuParentElement.style.top : `${y}`;
                            yAxis = parseFloat(topValue);
                        }

                        return {
                            x: xAxis,
                            y: yAxis,
                        };
                    },
                },
            ],
            whileElementsMounted: autoUpdate,
        });

        const hover = useHover(context, {
            delay: isNested ? userDelay : userDelay || useDelayGroup(context).delay,
            handleClose:
                typeof window !== "undefined" && typeof performance !== "undefined"
                    ? safePolygon({ blockPointerEvents: true })
                    : null,
            move: false,
        });
        const click = useClick(context, {
            event: "mousedown",
            toggle: !isNested,
            ignoreMouse: true,
        });
        const role = useRole(context, { role: "menu" });
        const dismiss = useDismiss(context, { bubbles: true, referencePress: true });
        const listNavigation = useListNavigation(context, {
            listRef: elementsRef,
            activeIndex,
            nested: isNested,
            onNavigate: setActiveIndex,
        });
        const typeahead = useTypeahead(context, {
            listRef: labelsRef,
            onMatch: isOpen ? setActiveIndex : undefined,
            activeIndex,
        });

        const { isMounted, styles: translationStyles } = useTransitionStyles(context, { duration: 0 });

        const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
            hover,
            click,
            role,
            dismiss,
            listNavigation,
            typeahead,
        ]);

        // Event emitter allows you to communicate across tree components.
        // This effect closes all menus when an item gets clicked anywhere
        // in the tree.
        React.useEffect(() => {
            if (!tree) {
                return;
            }

            function handleTreeClick() {
                setIsOpen(false);
            }

            function onSubMenuOpen(event: { nodeId: string; parentId: string }) {
                if (event.nodeId !== nodeId && event.parentId === parentId) {
                    setIsOpen(false);
                }
            }

            tree.events.on("click", handleTreeClick);
            tree.events.on("menuopen", onSubMenuOpen);

            return () => {
                tree.events.off("click", handleTreeClick);
                tree.events.off("menuopen", onSubMenuOpen);
            };
        }, [tree, nodeId, parentId]);

        React.useEffect(() => {
            if (isOpen && tree) {
                tree.events.emit("menuopen", { parentId, nodeId });
            }
        }, [tree, isOpen, nodeId, parentId]);

        return (
            <FloatingNode id={nodeId}>
                <div
                    ref={useMergeRefs([refs.setReference, item.ref, forwardedRef])}
                    tabIndex={!isNested ? undefined : parent.activeIndex === item.index ? 0 : -1}
                    role={isNested ? "menuitem" : "button"}
                    data-open={isOpen ? "" : undefined}
                    data-nested={isNested ? "" : undefined}
                    data-focus-inside={hasFocusInside ? "" : undefined}
                    className={isNested ? "MenuItem" : "RootMenu"}
                    aria-expanded={isNested ? undefined : isOpen ? "true" : "false"}
                    {...getReferenceProps(
                        parent.getItemProps({
                            ...props,
                            onFocus(event: React.FocusEvent<HTMLButtonElement>) {
                                props.onFocus?.(event);
                                setHasFocusInside(false);
                                parent.setHasFocusInside(true);
                            },
                        }),
                    )}
                >
                    {React.cloneElement(menuTrigger, {
                        "data-open": isOpen ? "" : undefined,
                    })}
                </div>
                {/* XNG-Changes */}
                {!autoCompleteOpen && (
                    <MenuContext.Provider
                        value={{
                            activeIndex,
                            setActiveIndex,
                            getItemProps,
                            setHasFocusInside,
                            isOpen,
                        }}
                    >
                        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                            {isOpen && isMounted && (
                                <FloatingPortal>
                                    <FloatingFocusManager
                                        context={context}
                                        modal={false}
                                        initialFocus={isNested ? -1 : 0}
                                        returnFocus={false}
                                    >
                                        <div
                                            ref={refs.setFloating}
                                            className="Menu"
                                            style={{
                                                ...floatingStyles,
                                                ...translationStyles,
                                            }}
                                            {...getFloatingProps()}
                                        >
                                            {children}
                                        </div>
                                    </FloatingFocusManager>
                                </FloatingPortal>
                            )}
                        </FloatingList>
                    </MenuContext.Provider>
                )}
            </FloatingNode>
        );
    },
);

interface MenuItemProps {
    targetComponent: React.ReactElement;
    disabled?: boolean;
}

export const MenuItem = React.forwardRef<
    HTMLButtonElement,
    MenuItemProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ targetComponent, disabled, className, ...props }, forwardedRef) => {
    const menu = React.useContext(MenuContext);
    const item = useListItem();
    const tree = useFloatingTree();
    const isActive = item.index === menu.activeIndex;

    return React.cloneElement(targetComponent, {
        ref: useMergeRefs([item.ref, forwardedRef]),
        type: "button",
        role: "menuitem",
        className: xClassNames.append(className, "MenuItem"),
        tabIndex: isActive ? 0 : -1,
        disabled,
        ...menu.getItemProps({
            onClick(event: React.MouseEvent<HTMLButtonElement>) {
                props.onClick?.(event);
                tree?.events.emit("click");
            },
            onFocus(event: React.FocusEvent<HTMLButtonElement>) {
                props.onFocus?.(event);
                menu.setHasFocusInside(true);
            },
        }),
    });
});

const FloutingUiMenu = React.forwardRef<HTMLButtonElement, MenuProps & React.HTMLProps<HTMLButtonElement>>(
    (props, ref) => {
        const parentId = useFloatingParentNodeId();

        if (parentId === null) {
            return (
                <FloatingTree>
                    <MenuComponent {...props} ref={ref} />
                </FloatingTree>
            );
        }

        return <MenuComponent {...props} ref={ref} />;
    },
);

export default FloutingUiMenu;
